<template>
  <v-container
    fluid
    class="py-0"
  >
    <top-bar
      header="Address"
      :breadcrumbs="[{ name: 'Address Book', to: { name: 'AddressBook' } }]"
    />
    <v-row>
      <v-col
        cols="12"
        md="9"
      >
        <div
          v-if="initialLoading"
          class="text-center"
        >
          <v-progress-circular
            color="primary"
            indeterminate
          />
        </div>
        <div
          v-else
          style="max-width: 700px"
        >
          <v-form
            @submit.prevent="save"
            v-model="valid"
          >
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  label="First Name"
                  :value="recordChanged.firstname"
                  @input="handleChange('record', 'firstname', $event)"
                  :rules="[v => !!v || 'Required']"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  label="Last Name"
                  :value="recordChanged.lastname"
                  @input="handleChange('record', 'lastname', $event)"
                  :rules="[v => !!v || 'Required']"
                />
              </v-col>
            </v-row>
            <v-text-field
              label="Phone"
              type="tel"
              :value="recordChanged.phone"
              @input="handleChange('record', 'phone', $event)"
              :rules="[v => !!v || 'Required', validPhone]"
              validate-on-blur
              @blur="removeNonNumerics"
            />
            <v-text-field
              label="Address"
              :value="(recordChanged.address || '').split('\n')[0]"
              @input="handleChange('record', 'address', $event + ((recordChanged.address || '').split('\n')[1] ? '\n' + recordChanged.address.split('\n')[1] : ''))"
              :rules="[v => !!v || 'Required']"
            />
            <v-text-field
              label="Apt / Suite"
              :value="(recordChanged.address || '').split('\n')[1]"
              @input="handleChange('record', 'address', (recordChanged.address || '').split('\n')[0] + '\n' + $event)"
            />
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  label="Country"
                  :value="recordChanged.country"
                  @input="handleChange('record', 'country', $event)"
                  :items="countryItems"
                  @change="changeCountry"
                  :rules="[v => !!v || 'Required']"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <v-select
                  label="State"
                  :value="recordChanged.state"
                  @input="handleChange('record', 'state', $event)"
                  :items="stateItems"
                  :prepend-icon="loadingStates ? 'fa-spinner fa-spin' : undefined"
                  :rules="[v => !!v || 'Required']"
                />
              </v-col>
            </v-row>
            <v-text-field
              label="City"
              :value="recordChanged.city"
              @input="handleChange('record', 'city', $event)"
              :rules="[v => !!v || 'Required']"
            />
            <v-text-field
              label="Postal Code"
              :value="recordChanged.zipcode"
              @input="handleChange('record', 'zipcode', $event)"
              :rules="[v => !!v || 'Required']"
            />
            <v-checkbox
              label="Make this my default billing address."
              :input-value="recordChanged.default_b === 'Y'"
              @click.stop="handleChange('record', 'default_b', recordChanged.default_b === 'Y' ? 'N' : 'Y')"
            />
            <v-checkbox
              label="Make this my default shipping address."
              :input-value="recordChanged.default_s === 'Y'"
              @click.stop="handleChange('record', 'default_s', recordChanged.default_s === 'Y' ? 'N' : 'Y')"
            />
            <div
              v-if="allChanges.length"
              class="text-right"
            >
              <v-btn
                type="submit"
                color="primary"
                :disabled="loading || !valid"
              >Save</v-btn>
            </div>
          </v-form>
        </div>
      </v-col>
      <v-col
        cols="12"
        md="3"
      >
        <account-manager />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Address, Country, State } from '@/models'
import { get } from 'lodash'
import createChangeTrackerMixin from '@/mixins/createChangeTrackerMixin'

export default {
  name: 'AddressEditor',
  mixins: [
    createChangeTrackerMixin({ path: 'record' })
  ],
  data () {
    return {
      initialLoading: false,
      loading: false,
      loadingStates: false,
      valid: false
    }
  },
  computed: {
    addressId () {
      return this.$route.params.addressId
    },
    record () {
      if (this.addressId === 'new') {
        return {}
      }
      return Address.find(this.addressId) || {}
    },
    countryItems () {
      return Country.all().map(r => ({ text: r.name, value: r.id }))
    },
    stateItems () {
      if (!(this.recordChanged && this.recordChanged.country)) {
        return []
      }
      return State.query()
        .where(r => r.country_code === this.recordChanged.country)
        .get()
        .map(r => ({ text: r.name, value: r.id }))
    }
  },
  async created () {
    if (!this.addressId) {
      this.$router.replace({ name: 'AddressBook' })
    }
    this.initialLoading = true
    try {
      await this.loadData()
      this.initialLoading = false
    } catch (e) {
      this.initialLoading = false
      throw e
    }
    if (this.$route.query && this.$route.query.default_b) {
      this.handleChange('record', 'default_b', 'Y')
    }
    if (this.$route.query && this.$route.query.default_s) {
      this.handleChange('record', 'default_s', 'Y')
    }
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          ...(this.addressId && this.addressId !== 'new' ? [Address.fetchOne(this.addressId, { force: true })] : []),
          ...(this.countryItems.length ? [] : [Country.fetchMany()]),
        ])
        this.loadStates()
        this.loading = false
      } catch (e) {
        this.loading = false
        throw e
      }
    },
    async save () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        const result = await Address.save(this.recordChanged)
        if (this.addressId === 'new' && get(result, 'entities.address[0].id')) {
          await this.$router.replace({ params: { addressId: result.entities.address[0].id } })
        }
        this.clearChanges()
        this.$router.push({ name: 'AddressBook' })
        this.loading = false
      } catch (e) {
        this.loading = false
        throw e
      }
    },
    async changeCountry () {
      this.handleChange('record', 'state', null)
      await this.loadStates()
    },
    async loadStates () {
      if (!this.stateItems.length && this.recordChanged.country) {
        await State.fetchMany({
          params: {
            country: this.recordChanged.country
          }
        })
      }
    },
    validPhone (v) {
      const number = ((v || '') + '').replace(/[^0-9]+/g, '')
      return (number && (number.length === 10 || (number.startsWith('1') && number.length === 11))) || 'That number is not valid'
    },
    removeNonNumerics () {
      this.handleChange('record', 'phone', ((this.recordChanged.phone || '') + '').replace(/[^0-9]+/g, ''))
    }
  }
}
</script>
